
































import mixins from 'vue-typed-mixins'
import { mapGetters } from 'vuex'
import { orderBy } from 'lodash'
import WindowResizeListener from '@/abstract/WindowResizeListener'
import BaseLayout from '@/layout/BaseLayout.vue'
import EmptyResult from '@/components/EmptyResult/EmptyResult.vue'
import ColumnHeading from '@/components/Common/ColumnHeading.vue'
import ScriptsHeader from '@/components/Scripts/ScriptsHeader/ScriptsHeader.vue'
import {
  ScriptTemplateItemDesktop,
  ScriptTemplateItemTablet,
  ScriptTemplateItemMobile,
} from './components/ScriptTemplateItem/ScriptTemplateItem'
import store from '@/store'
import { ScriptTemplatesAction } from '@/store/modules/script-templates/actions'
import { ESortDir } from '@/constants/dataTable'

import type { RawLocation, Route } from 'vue-router'
import type { ScriptTemplate } from '@/models/scriptTemplates'

interface Column {
  key: keyof ScriptTemplate
  label: string
  sortable: boolean
}

interface Data {
  columns: Column[]
  sortBy: keyof ScriptTemplate
  sortDir: ESortDir
  search: string
}

interface Methods {
  getSortBy: (key: keyof ScriptTemplate) => ESortDir | undefined
  handleSort: (key: keyof ScriptTemplate) => void
  handleSearch: (text: string) => void
  handleClearSearch: () => void
}

interface Computed {
  scriptTemplates: ScriptTemplate[]
  preparedScriptTemplates: ScriptTemplate[]
  componentName: string
}

async function getScriptTemplates(
  to: Route,
  from: Route,
  next: (to?: RawLocation) => void,
) {
  try {
    await store.dispatch(ScriptTemplatesAction.getScriptTemplates)
  } catch (e) {
    console.log(e)
  }

  next()
}

export default mixins(WindowResizeListener).extend<Data, Methods, Computed, {}>({
  beforeRouteEnter: getScriptTemplates,
  beforeRouteUpdate: getScriptTemplates,

  components: {
    BaseLayout,
    EmptyResult,
    ScriptsHeader,
    ColumnHeading,
    ScriptTemplateItemDesktop,
    ScriptTemplateItemTablet,
    ScriptTemplateItemMobile,
  },
  data() {
    return {
      columns: [
        {
          key: 'id',
          label: '№',
          sortable: true,
        },
        {
          key: 'title',
          label: this.$tc('scriptTemplate.name'),
          sortable: true,
        },
        {
          key: 'theme',
          label: this.$tc('scriptTemplate.theme'),
          sortable: true,
        },
        {
          key: 'tags',
          label: this.$tc('scriptTemplate.tags'),
          sortable: true,
        },
        {
          key: 'date',
          label: this.$tc('scriptTemplate.date'),
          sortable: true,
        },
      ],
      sortBy: 'id',
      sortDir: ESortDir.ASC,
      search: '',
    }
  },
  computed: {
    ...mapGetters({
      scriptTemplates: 'getScriptTemplates',
    }),
    preparedScriptTemplates(): ScriptTemplate[] {
      const filteredScriptTemplates = this.search ? this.scriptTemplates.filter(template => {
        return (
          template.title.toLowerCase().includes(this.search)
          || template.theme.toLowerCase().includes(this.search)
          || template.tags.some(tag => `#${tag}`.toLowerCase().includes(this.search))
        )
      }) : this.scriptTemplates

      if (this.sortBy && this.sortDir) {
        return orderBy(filteredScriptTemplates, this.sortBy, this.sortDir)
      }
      return filteredScriptTemplates
    },
    componentName(): string {
      if (this.isDesktop) {
        return 'ScriptTemplateItemDesktop'
      }
      if (this.isTablet) {
        return 'ScriptTemplateItemTablet'
      }
      return 'ScriptTemplateItemMobile'
    },
  },
  methods: {
    getSortBy(key: keyof ScriptTemplate): ESortDir | undefined {
      return this.sortBy === key ? this.sortDir : undefined
    },
    handleSort(key: keyof ScriptTemplate): void {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === ESortDir.ASC ? ESortDir.DESC : ESortDir.ASC
        return
      }

      this.sortBy = key
      this.sortDir = ESortDir.ASC
    },
    handleSearch(text: string): void {
      this.search = text.toLowerCase()
    },
    handleClearSearch(): void {
      this.search = ''
    },
  },
  metaInfo() {
    return { title: this.$tc('scriptTemplate.title', 2) }
  },
})
